import axios, { AxiosError } from "axios";

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:4072/api/v1";

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Every GET carries a unique parameter so the browser cannot answer it from
// cache. This exists because production served API responses with
// `Cache-Control: public, max-age=31536000, immutable` — a static-asset policy
// applied to live JSON. Under `immutable` the browser does not even send a
// revalidation request, so a deleted artist kept coming back from cache and
// every edit looked like it had not saved. A distinct URL is the only thing
// that reliably defeats that, and unlike a request header it needs no CORS
// change.
//
// The server now sends `no-store`, so once the proxy stops overriding it this
// is belt-and-braces and can be removed. Unknown query keys are stripped by the
// zod schemas, so `_t` never reaches a handler.
api.interceptors.request.use((config) => {
  if ((config.method ?? "get").toLowerCase() === "get") {
    config.params = { ...(config.params ?? {}), _t: Date.now() };
  }
  return config;
});

let refreshPromise: Promise<void> | null = null;

async function refreshSession(): Promise<void> {
  refreshPromise ??= api
    .post("/auth/refresh")
    .then(() => undefined)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (typeof error.config & { _retry?: boolean })
      | undefined;
    const isAuthEndpoint =
      originalRequest?.url?.includes("/auth/login") ||
      originalRequest?.url?.includes("/auth/refresh");

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;
      try {
        await refreshSession();
        return api(originalRequest);
      } catch {
        window.dispatchEvent(new CustomEvent("auth:logout"));
      }
    }

    return Promise.reject(error);
  },
);

export interface ApiErrorPayload {
  success: false;
  error: { code: string; message: string; details?: unknown };
}

export function getErrorMessage(
  error: unknown,
  fallback = "Something went wrong",
): string {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as ApiErrorPayload | undefined;
    return payload?.error?.message ?? fallback;
  }
  return fallback;
}
